var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { produce } from 'immer';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { conversationsApi, } from 'apis/conversations';
import { userContentApi } from 'apis/userContent';
import { Sentry, SentryTeam } from 'app/utils/sentry';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
/**
 * A hook for reading conversation while also patching it, for example by
 * fetching and patching email events with their respective original email
 * content.
 */
export const useReadPatchedConversation = () => {
    const dispatch = useAppDispatch();
    const [patchedResponse, setPatchedResponse] = useState();
    const [patchAsyncStatus, setPatchAsyncStatus] = useState('uninitialized');
    const [readUnpatchedConversation, { data: unpatchedConversationData, error, isFetching, isError, isUninitialized, },] = conversationsApi.useLazyReadConversationQuery();
    /** Initiates patching the read conversation response. */
    const patchResponse = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        if (!unpatchedConversationData) {
            return;
        }
        setPatchAsyncStatus('pending');
        const patchedEvents = yield Promise.all(unpatchedConversationData.data.history.main_conversation.events.map((event) => new Promise((resolve) => {
            // Handles patching inbound email message added events by fetching
            // their original email content from another endpoint and
            // rewriting the event text field.
            if (event.type === 'conversation:message_added' &&
                (event.channel === 'contact_form' ||
                    event.channel === 'email') &&
                event.direction === 'inbound' &&
                event.email_original_content_url) {
                dispatch(userContentApi.endpoints.readOriginalEmail.initiate({
                    path: event.email_original_content_url,
                }))
                    .then((response) => {
                    resolve(produce(event, (draft) => {
                        var _a, _b;
                        draft.text =
                            (_b = (_a = response.data) === null || _a === void 0 ? void 0 : _a.data.msg.rewritten_html) !== null && _b !== void 0 ? _b : draft.text;
                    }));
                })
                    .catch((error) => {
                    // If for some reason the endpoint errors out, we fallback
                    // to the original event because we have already it, and
                    // log to Sentry for further investigation.
                    resolve(event);
                    Sentry.captureException(error, {
                        tags: {
                            team: SentryTeam.Conversations,
                        },
                    });
                });
            }
            else {
                resolve(event);
            }
        })));
        setPatchAsyncStatus('fulfilled');
        setPatchedResponse(produce(unpatchedConversationData, (draft) => {
            draft.data.history.main_conversation.events = patchedEvents;
        }));
    }), [dispatch, unpatchedConversationData]);
    /**
     * Represents the cumulative async status of reading and patching the
     * conversation.
     */
    const asyncStatus = useMemo(() => {
        if (isUninitialized || patchAsyncStatus === 'uninitialized') {
            return 'uninitialized';
        }
        if (isFetching || patchAsyncStatus === 'pending') {
            return 'pending';
        }
        if (isError) {
            return 'rejected';
        }
        return 'fulfilled';
    }, [isError, isFetching, isUninitialized, patchAsyncStatus]);
    /** Invokes reading the conversation. */
    const readConversation = useCallback((arg) => {
        readUnpatchedConversation(arg);
    }, [readUnpatchedConversation]);
    // Handles patching the main conversation.
    useEffect(() => {
        if (unpatchedConversationData) {
            patchResponse();
        }
    }, [unpatchedConversationData, patchResponse]);
    return [
        readConversation,
        {
            data: patchedResponse,
            error,
            asyncStatus,
        },
    ];
};
